import React, { useEffect, useRef, useState } from 'react';
import useSafariBfCacheInvalidator from '../hooks/useSafariBfCacheInvalidator';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { Spinner } from '../root/components/Spinner';
import { PhoneCodeFactor } from '@clerk/types/dist/factors';
import { cleanPhone } from '../util';
import { ClerkResource } from '../models/ClerkResource.model';
import styles from './LoyaltyForm.module.scss';
import { to } from 'await-to-js';
import { Link } from '../root/components/Link';
import { cleanClerkError } from './clerkHelper';
import { Button } from '../root/components/buttons/Button';

declare const window: HoneyloveWindow;

interface Props {
  phone: string;
  startOver: (error?: string) => void;
  clerkResource: ClerkResource;
}

const captureError = (err: any, identifier: string) => {
  err._clerkErrors = JSON.stringify(
    {
      errors: err.errors,
      clerkIdentifier: identifier,
    },
    null,
    2,
  );
  window.Sentry.captureException(err);
};

export const PhoneOtcValidation: React.FC<Props> = ({ phone, clerkResource, startOver }) => {
  const [oneTimeCode, setOneTimeCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  useSafariBfCacheInvalidator(setIsLoading);
  const [localError, setLocalError] = useState<string>();
  const phoneInput = useRef<HTMLInputElement>(null);

  const reset = (err?: string) => {
    setLocalError('');
    startOver(err);
  };

  const preparePhoneOtc = async () => {
    const cleanIdentifier = cleanPhone(phone);

    if (clerkResource.type === 'signin') {
      const [signInErr, signInAttempt] = await to(
        clerkResource.signIn.create({
          identifier: cleanIdentifier,
        }),
      );
      if (signInErr || !signInAttempt) {
        reset(cleanClerkError(signInErr, 'Error during sign-in, please try again'));
        return;
      }

      const phoneStrategy = signInAttempt.supportedFirstFactors.find(({ strategy }) => strategy === 'phone_code') as PhoneCodeFactor;
      const phoneId = phoneStrategy.phoneNumberId;
      const [firstFactorErr] = await to(
        signInAttempt.prepareFirstFactor({
          strategy: 'phone_code',
          phoneNumberId: phoneId,
        }),
      );

      if (firstFactorErr) {
        reset(cleanClerkError(firstFactorErr, 'Error sending one-time code, please try again'));
        return;
      }
    } else {
      // handle signup
      const [signUpErr, signUpAttempt] = await to(
        clerkResource.signUp.create({
          phoneNumber: phone,
          firstName: clerkResource.firstName,
          lastName: clerkResource.lastName,
        }),
      );

      if (signUpErr || !signUpAttempt) {
        reset(cleanClerkError(signUpErr, 'Error occurred while signing up, please try again'));
        return;
      }

      const [prepareErr] = await to(signUpAttempt.preparePhoneNumberVerification({ strategy: 'phone_code' }));
      if (prepareErr) {
        reset(cleanClerkError(prepareErr, 'A signup error occurred while sending one-time code, please try again'));
        return;
      }
    }

    setIsLoading(false);
    phoneInput?.current?.focus();
  };

  const submitOTC = async () => {
    setIsLoading(true);

    const identifier = clerkResource.type === 'signin' ? clerkResource.signIn.identifier : clerkResource.signUp.phoneNumber;

    if (oneTimeCode) {
      const [err, verifyResponse] =
        clerkResource.type === 'signin'
          ? await to(
              clerkResource.signIn.attemptFirstFactor({
                code: oneTimeCode,
                strategy: 'phone_code',
              }),
            )
          : await to(
              clerkResource.signUp.attemptVerification({
                code: oneTimeCode,
                strategy: 'phone_code',
              }),
            );

      if (err || !verifyResponse) {
        captureError(err, identifier ?? '');
        if ((err as any)?.errors) {
          setOneTimeCode('');
          setLocalError('Invalid code');
          setIsLoading(false);
        } else {
          throw err;
        }
        return;
      }

      if (verifyResponse.status === 'complete') {
        window.location.href = '/pages/profile';
      }
    }
  };

  React.useEffect(() => {
    // Autosubmit code once inputted
    if (oneTimeCode?.length === 6) {
      submitOTC();
    }
  }, [oneTimeCode]);

  useEffect(() => {
    preparePhoneOtc();
  }, []);

  if (isLoading) {
    return <Spinner visible={true} light={false} styleOverrides={{ spinner: { top: '100px' } }} />;
  }

  return (
    <>
      <div className={styles.otpInputLabel}>
        <h2>Enter the 6-digit code sent to:</h2>
        <div>{phone}</div>
      </div>
      <input
        ref={phoneInput}
        type="number"
        className={styles.otcInput}
        placeholder={'000000'}
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete={'off'}
        value={oneTimeCode}
        onKeyPress={(e) => {
          if (oneTimeCode?.length === 6) {
            e.preventDefault();
          }
        }}
        onChange={(e) => {
          setOneTimeCode(e.target.value.substring(0, 6));
        }}
      />
      <Button fullWidth={true} onClick={() => reset()} text="Start over" buttonType="secondary" />
      {localError && <div className={styles.error}>{localError}</div>}
    </>
  );
};

export default PhoneOtcValidation;
